import { create } from "zustand"
import { type IEnergyPassportProduct, IEnergyPassportResourceType } from "@/entities/energy-passport/types.ts"
import { type IEnergyPassportDetailsStoreValue, useEnergyPassportDetailsStore } from "@/features/energy-passport/ui/detail-tables/model/store.ts"
import { initialEnergyPassportProducts } from "@/features/energy-passport/ui/main-table/model/static-data.ts"
import { quarterNumbers } from "@/shared/constants"
import { generateTempId } from "@/shared/lib/helper-functions.ts"
import type { IQuarterNumber } from "@/shared/types/interfaces"

const emptyProduct: IEnergyPassportProduct = {
  nameError: true, // Initially there is no name
  quarter_number: 0 as IQuarterNumber,
  product_name: "",
  count: 0,
  count_measure: "",
  active_energy: 0,
  reactive_energy: 0,
  thermal_energy: 0,
  gas: 0,
  mazut: 0,
  coal: 0,
  gasoline: 0,
  diesel: 0,
  kerosene: 0,
  // oils: 0,
  other_petroleum_products: 0,
  water: 0,
  is_main_product: false
}

export type IEnergyPassportProductEditableField = keyof Omit<IEnergyPassportProduct, "id" | "is_main_product" | "quarter" | "nameError">

export interface IEnergyPassportQuarter {
  products: IEnergyPassportProduct[]
  expanded: boolean
  quarterNumber: IQuarterNumber
  deletedProductIds: number[]
}

interface IEnergyPassportStore {
  quarters: IEnergyPassportQuarter[]
  invalidProduct: IEnergyPassportProduct | null
  isOtherQuarterTouched: boolean // This is TRUE when 2,3,4 quarter's product is touched
}

interface IEnergyPassportActions {
  setQuarters: (quarters: IEnergyPassportQuarter[]) => void
  updateExternalDetailsStore: (quarterNumber: IQuarterNumber, quarters: IEnergyPassportQuarter[], fieldName: keyof IEnergyPassportProduct) => void
  toggleQuarterRow: (quarterNumber: IQuarterNumber) => void
  updateProduct: (quarterNumber: IQuarterNumber, editedProduct: Partial<IEnergyPassportProduct>, fieldName?: keyof IEnergyPassportProduct) => void
  addProduct: (quarterNumber: IQuarterNumber) => void
  removeProduct: (quarterNumber: IQuarterNumber, id: number | string, isSavedProduct: boolean) => void
  notifyInvalidProduct: (product: IEnergyPassportProduct | null) => void
  setIsOtherQuarterTouched: (touched: boolean) => void
}

export const energyPassportInitialQuarterData = quarterNumbers.map(quarterNumber => ({
  quarterNumber,
  expanded: false,
  products: initialEnergyPassportProducts.map((p, index) => ({ ...p, tempId: `id-${index}` })),
  deletedProductIds: []
}))

const initial: IEnergyPassportStore = {
  quarters: energyPassportInitialQuarterData,
  invalidProduct: null,
  isOtherQuarterTouched: false
}

export type IEnergyPassportMainStore = IEnergyPassportStore & IEnergyPassportActions
export const useEnergyPassportStore = create<IEnergyPassportMainStore>()((set, get) => ({
  ...initial,
  setQuarters: quarters => set({ quarters }),
  setIsOtherQuarterTouched: isOtherQuarterTouched => set({ isOtherQuarterTouched }),
  toggleQuarterRow: quarterNumber =>
    set({
      quarters: get().quarters.map(q =>
        q.quarterNumber === quarterNumber
          ? {
              ...q,
              expanded: !q.expanded
            }
          : q
      )
    }),
  updateExternalDetailsStore: (quarterNumber, quarters, fieldName) => {
    const detailsStore = useEnergyPassportDetailsStore.getState()
    const allowedFields: (keyof IEnergyPassportProduct)[] = [
      "product_name",
      "active_energy",
      "reactive_energy",
      "thermal_energy",
      "gas",
      "mazut",
      "coal",
      "gasoline",
      "diesel",
      "kerosene",
      "other_petroleum_products",
      "water"
    ]

    if (allowedFields.includes(fieldName)) {
      const type = determineResourceType(fieldName)

      if (type) {
        const products = quarters.find(q => q.quarterNumber === quarterNumber)?.products
        const technical_needs =
          products?.reduce((total, product) => {
            const value = product[fieldName]
            return typeof value === "number" ? total + value : total
          }, 0) ?? 0
        detailsStore.updateQuarter(type, {
          quarter: quarterNumber,
          technical_needs
        })
      }
    }
  },
  updateProduct: (quarterNumber, editedProduct, fieldName) => {
    const isFirstQuarter = quarterNumber === 1
    let quarters: IEnergyPassportQuarter[] = []
    console.log({ editedProduct })
    console.log({ a: get().isOtherQuarterTouched })
    if (isFirstQuarter && !get().isOtherQuarterTouched) {
      quarters = get().quarters.map(q => ({
        ...q,
        products: q.products.map(product => {
          console.log("{ product }")
          console.log({ product })
          const found = editedProduct.id ? product.id === editedProduct.id : product.tempId === editedProduct.tempId
          return found ? { ...product, ...editedProduct } : product
        })
      }))
    } else {
      quarters = get().quarters.map(q => {
        if (q.quarterNumber === quarterNumber) {
          return {
            ...q,
            products: q.products.map(product => {
              const found = editedProduct.id ? product.id === editedProduct.id : product.tempId === editedProduct.tempId
              return found ? { ...product, ...editedProduct } : product
            })
          }
        }
        return q
      })
    }

    if (quarterNumber > 1) get().setIsOtherQuarterTouched(true)
    if (fieldName) {
      get().updateExternalDetailsStore(quarterNumber, quarters, fieldName)
    }
    set({ quarters })
  },
  addProduct: quarterNumber => {
    const isFirstQuarter = quarterNumber === 1
    let quarters: IEnergyPassportQuarter[] = []

    if (isFirstQuarter && !get().isOtherQuarterTouched) {
      const tempId = generateTempId("product")
      quarters = get().quarters.map(q => ({
        ...q,
        products: q.products.concat({ ...emptyProduct, tempId })
      }))
    } else {
      quarters = get().quarters.map(q => {
        if (q.quarterNumber === quarterNumber) {
          return {
            ...q,
            products: q.products.concat({ ...emptyProduct, tempId: generateTempId("product") })
          }
        }
        return q
      })
    }
    set({ quarters })
  },
  removeProduct: (quarterNumber, id, isSavedProduct) => {
    const isFirstQuarter = quarterNumber === 1
    let quarters: IEnergyPassportQuarter[] = []

    console.log({ quarterNumber, id, isSavedProduct })

    if (isFirstQuarter && !get().isOtherQuarterTouched) {
      quarters = get().quarters.map(q => ({
        ...q,
        deletedProductIds: isSavedProduct ? [...q.deletedProductIds, id as number] : q.deletedProductIds,
        products: q.products.filter(p => (isSavedProduct ? p.id !== id : p.tempId !== id))
      }))
    } else {
      quarters = get().quarters.map(q => {
        if (q.quarterNumber === quarterNumber) {
          return {
            ...q,
            deletedProductIds: isSavedProduct ? [...q.deletedProductIds, id as number] : q.deletedProductIds,
            products: q.products.filter(p => (isSavedProduct ? p.id !== id : p.tempId !== id))
          }
        }
        return q
      })
    }

    // Update the details store ( useEnergyPassportDetailsStore )
    const detailsStore = useEnergyPassportDetailsStore.getState()
    if (detailsStore) {
      const all = Object.entries(detailsStore) as [IEnergyPassportResourceType, IEnergyPassportDetailsStoreValue][]
      const updated: [IEnergyPassportResourceType, IEnergyPassportDetailsStoreValue][] = all.map(([type, itemObject]) => {
        return [
          type,
          {
            ...itemObject,
            quarters: itemObject.quarters?.map(quarter => {
              const products = quarters.find(q => q.quarterNumber === quarterNumber)?.products

              const technical_needs =
                products?.reduce((total, product) => {
                  const fieldName = determineFieldType(type)
                  const value = fieldName ? product[fieldName] : 0
                  return typeof value === "number" ? total + value : total
                }, 0) ?? 0

              return {
                ...quarter,
                technical_needs
              }
            })
          }
        ]
      })

      // console.log("updated")
      // console.log(updated)
      // console.log(Object.fromEntries(updated))

      // Now update the details store
      try {
        useEnergyPassportDetailsStore.getState().setDetails(Object.fromEntries(updated))
      } catch (err) {
        console.log(err)
      }
    }
    set({ quarters })
  },
  notifyInvalidProduct: invalidProduct =>
    set({
      invalidProduct
    })
}))

function determineResourceType(fieldName: keyof IEnergyPassportProduct): IEnergyPassportResourceType | null {
  switch (fieldName) {
    case "active_energy":
      return IEnergyPassportResourceType.ACTIVE_ELECTRICITY
    case "reactive_energy":
      return IEnergyPassportResourceType.REACTIVE_ELECTRICITY
    case "thermal_energy":
      return IEnergyPassportResourceType.HEAT
    case "gas":
      return IEnergyPassportResourceType.GAS
    case "mazut":
      return IEnergyPassportResourceType.MAZUT
    case "coal":
      return IEnergyPassportResourceType.COAL
    case "gasoline":
      return IEnergyPassportResourceType.PETROL
    case "diesel":
      return IEnergyPassportResourceType.DIESEL
    case "kerosene":
      return IEnergyPassportResourceType.KEROSENE
    // case "oils":
    //   return IEnergyPassportResourceType.LUBRICANT
    case "other_petroleum_products":
      return IEnergyPassportResourceType.OTHER_OIL_PRODUCT
    case "water":
      return IEnergyPassportResourceType.WATER
  }
  return null
}

function determineFieldType(fieldName: IEnergyPassportResourceType): keyof IEnergyPassportProduct | null {
  switch (fieldName) {
    case IEnergyPassportResourceType.ACTIVE_ELECTRICITY:
      return "active_energy"
    case IEnergyPassportResourceType.REACTIVE_ELECTRICITY:
      return "reactive_energy"
    case IEnergyPassportResourceType.HEAT:
      return "thermal_energy"
    case IEnergyPassportResourceType.GAS:
      return "gas"
    case IEnergyPassportResourceType.MAZUT:
      return "mazut"
    case IEnergyPassportResourceType.COAL:
      return "coal"
    case IEnergyPassportResourceType.PETROL:
      return "gasoline"
    case IEnergyPassportResourceType.DIESEL:
      return "diesel"
    case IEnergyPassportResourceType.KEROSENE:
      return "kerosene"
    // case IEnergyPassportResourceType.LUBRICANT:
    //   return "oils"
    case IEnergyPassportResourceType.OTHER_OIL_PRODUCT:
      return "other_petroleum_products"
    case IEnergyPassportResourceType.WATER:
      return "water"
  }
  return null
}
